import { Selection } from '@tiptap/pm/state';
import { useCurrentEditor } from '@tiptap/react';
import { useEffect, useState } from 'react';

interface ActiveSize {
  width: number;
  height: number;
  top: number;
  left: number;
}

export const ImageSizeBadge = () => {
  const { editor } = useCurrentEditor();
  const [size, setSize] = useState<ActiveSize | null>(null);

  useEffect(() => {
    if (!editor || !editor.isEditable) {
      return;
    }

    let activeImg: HTMLImageElement | null = null;

    const measure = (): ActiveSize | null => {
      if (!activeImg) {
        return null;
      }

      // Position the badge relative to the editor wrapper so it shares the editor's
      // overflow: hidden and can't escape into surrounding chrome.
      const editorEl = editor.view.dom.closest('.tiptap-editor');
      if (!editorEl) {
        return null;
      }

      const rect = activeImg.getBoundingClientRect();
      const editorRect = editorEl.getBoundingClientRect();
      return {
        width: Math.round(rect.width),
        height: Math.round(rect.height),
        top: rect.bottom - editorRect.top + 6,
        left: rect.right - editorRect.left,
      };
    };

    const onMove = () => {
      const next = measure();
      if (next) {
        setSize(next);
      }
    };
    const onUp = () => {
      activeImg = null;
      setSize(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp, true);
    };
    const onTouchEnd = () => {
      // Upstream ResizableNodeView registers touchstart/touchmove but no touchend, and its
      // touchstart handler preventDefaults so no synthesized mouseup follows. Without this,
      // a mobile drag leaves data-resize-state="true" forever, never fires onCommit, and the
      // image stays selected. Synthesize a mouseup on document to drive the library's
      // cleanup path before we clear our own state.
      const stuck = document.querySelector('[data-resize-container][data-resize-state="true"]');
      if (stuck) {
        document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
        // The library's onCommit calls setNodeSelection, leaving the image as a node
        // selection so .ProseMirror-selectednode keeps the outline/handle visible. On
        // desktop the user clicks away; on mobile drop the selection ourselves.
        // Selection.near finds a valid neighbouring position; a raw setTextSelection
        // at selection.to lands at doc level when the image is the last block and
        // warns "TextSelection endpoint not pointing into a node with inline content".
        // Clamp `to` because doc.resolve throws on positions past doc.content.size.
        const { state, view } = editor;
        const pos = Math.min(state.selection.to, state.doc.content.size);
        view.dispatch(state.tr.setSelection(Selection.near(state.doc.resolve(pos))));
      }
      activeImg = null;
      setSize(null);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onTouchEnd, true);
      window.removeEventListener('touchcancel', onTouchEnd, true);
    };
    const trackHandle = (target: HTMLElement | null): boolean => {
      if (!target?.closest('[data-resize-handle]')) {
        return false;
      }

      if (!editor.view.dom.contains(target)) {
        return false;
      }

      const img = target.closest('[data-resize-wrapper]')?.querySelector('img');
      if (!(img instanceof HTMLImageElement)) {
        return false;
      }

      activeImg = img;
      const initial = measure();
      if (initial) {
        setSize(initial);
      }
      return true;
    };
    // The resize handle's own mousedown/touchstart calls stopPropagation, so a bubble-phase
    // listener on the editor never fires. Capture phase on the document runs before the
    // library's handler and lets us pick up the drag start.
    const onDown = (event: MouseEvent) => {
      if (!trackHandle(event.target as HTMLElement | null)) {
        return;
      }

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, true);
    };
    const onTouchStart = (event: TouchEvent) => {
      if (!trackHandle(event.target as HTMLElement | null)) {
        return;
      }

      window.addEventListener('touchmove', onMove);
      window.addEventListener('touchend', onTouchEnd, true);
      window.addEventListener('touchcancel', onTouchEnd, true);
    };

    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('touchstart', onTouchStart, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('touchstart', onTouchStart, true);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp, true);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onTouchEnd, true);
      window.removeEventListener('touchcancel', onTouchEnd, true);
    };
  }, [editor]);

  if (!size) {
    return null;
  }

  return (
    <div className="tiptap-image-size-badge" style={{ top: size.top, left: size.left }}>
      {size.width} × {size.height}
    </div>
  );
};
